import React, { useState } from 'react'
import { Link, PageProps, graphql } from 'gatsby'
import { GatsbyImage, ImageDataLike, StaticImage, getImage, getSrc } from 'gatsby-plugin-image'
import { LuPlus } from 'react-icons/lu'

import { ContactForm, Container, Dialog, FAQ, FeaturedCard, Plans, Seo, WhatsAppButton } from 'components'
import { LandingPageLayout } from 'components/layouts'
import { useAnalytics } from 'hooks'
import { findContrastColor } from 'services/color'
import { getWindow } from 'services/window'

export const Head = ({ data }: PageProps<Queries.LandingPageQuery>) => {
  const { lp } = data
  if (!lp) return null

  const image16x9 = getSrc(lp.headerLogo?.localFile as ImageDataLike)

  return (
    <Seo
      seo={{
        shareImageUrl: image16x9,
        metaTitle: `Parceria ${lp.partner?.name}`,
        metaDescription: `${lp.primarySectionTitle} ${lp.primarySectionText}`,
      }}
    />
  )
}

export const PartnershipLandingPage = ({ data }: PageProps<Queries.LandingPageQuery>) => {
  const { lp, globalData } = data
  if (!lp) return null

  const { fireEvent } = useAnalytics()

  const [isPartnershipDisabledDialogOpen, setIsPartnershipDisabledDialogOpen] = useState<boolean>(false)

  const partnerLogo = getImage(lp.headerLogo?.localFile as ImageDataLike)
  const highlightImage = getImage(lp.highlightImage?.localFile as ImageDataLike)

  const primaryColor = lp.primaryColor || 'var(--color-primary-600)'
  const secondaryColor = lp.secondaryColor || 'var(--color-primary-300)'
  const primaryColorContrastText = findContrastColor(lp.primaryColor || '#FFFFFF') || 'inhreit'
  const secondaryColorContrastText = findContrastColor(lp.secondaryColor || '#FFFFFF') || 'inherit'

  const hasFreeDigitalCertificate = !lp.pricing?.digitalCertificate || lp.pricing?.digitalCertificate.value === 0

  const handleContactError = (error: any) => {
    if (!error || !error.response?.errors) return
    const [e] = error.response.errors

    if (e?.code === 'partnership_not_enabled') {
      setIsPartnershipDisabledDialogOpen(true)
    }
  }

  const handlePartnershipDisabledClick = () => {
    if (!lp.contactForm?.partnershipNotEnabledLink) return

    const w = getWindow()
    w?.open(lp.contactForm?.partnershipNotEnabledLink, '__blank', 'noreferrer,noopener')
  }

  // Disparo do evento de clique em CTA
  const fireAnalyticsEvent = (target: string) => {
    fireEvent('ctaClick', {
      component: 'PartnershipLandingPage',
      target,
    })
  }

  return (
    <LandingPageLayout whatsAppContextValue={{ customMessageSlug: lp.slug || undefined }}>
      <div className='bg-white py-2'>
        <Container>
          <div className='flex flex-row items-center justify-around w-full gap-x-4'>
            <WhatsAppButton onClick={() => fireAnalyticsEvent('linkWhatsapp')} />
            <Link to='/'>
              <StaticImage
                className=''
                backgroundColor='white'
                src='../images/logos/logo-black.png'
                width={200}
                aspectRatio={1.77777777}
                layout='constrained'
                transformOptions={{ cropFocus: 'center', fit: 'contain' }}
                quality={100}
                placeholder='blurred'
                alt='Logo Hubs Contabilidade'
              />
            </Link>

            <LuPlus size='3rem' />
            {partnerLogo && <GatsbyImage className='' image={partnerLogo} alt={`Logo parceiro ${lp.partner?.name}`} />}
          </div>
        </Container>
      </div>
      <Container className='py-10 lg:py-24'>
        <header className='w-full flex flex-row items-center justify-around'>
          <section className='md:w-2/3 flex flex-col items-start justify-center z-10'>
            <div className='flex flex-col gap-4 md:gap-8 md:max-w-[675px] mb-10'>
              <div>
                <h1 className='font-semibold text-2xl md:text-3xl xl:text-4xl md:leading-10'>{lp.title}</h1>
                <h2 className='font-light text-lg pt-2 leading-tight'>{lp.subtitle}</h2>
              </div>
              {lp.highlightText && (
                <div
                  className='px-2 py-1 text-sm w-fit'
                  style={{ backgroundColor: secondaryColor || 'var(--color-primary-600)', color: secondaryColorContrastText }}
                >
                  <p className='font-medium text-md'>{lp.highlightText}</p>
                </div>
              )}
            </div>

            <a href='#contato' className='w-fit' onClick={() => fireAnalyticsEvent('#contato')}>
              <button
                className='my-4 px-24 py-2 text-sm shadow-sm rounded-md font-medium drop-shadow-md'
                style={{ background: primaryColor, color: primaryColorContrastText }}
              >
                Eu quero
              </button>
            </a>
          </section>
          <StaticImage
            src='../images/icons/champion.svg'
            alt='Ícone de Boneco Comemorando'
            className='absolute md:relative opacity-50 md:opacity-100 mt-20 md:mt-0 right-0 max-w-1/2 z-0'
            width={300}
            placeholder='blurred'
          />
        </header>

        <section>
          <h3 className='font-semibold text-3xl leading-10'>{lp.primarySectionTitle}</h3>
          <p className='font-light text-lg pt-2 leading-tight'>{lp.primarySectionText}</p>
          <div className='mt-10 lg:place-items-start grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-8'>
            {lp.primaryCards
              ?.filter(Boolean)
              .map((c, i) => (
                <FeaturedCard
                  bgColor={secondaryColor}
                  borderColor={primaryColor}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  card={c!}
                  key={i}
                />
              ))}
          </div>
        </section>

        <section className='mt-20 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-5'>
          <div className='text-center flex flex-col'>
            <p className='text-left font-light text-lg pt-2 pb-6 leading-tight order-2 sm:order-1'>{lp.secondarySectionText}</p>
            <div className='order-1 sm:order-2 flex items-center justify-center'>
              {highlightImage && <GatsbyImage image={highlightImage} alt={`Ilustração parceiro ${lp.partner?.name}`} />}
            </div>
          </div>
          <div className='flex flex-col gap-4'>
            {lp.secondaryCards
              ?.filter(Boolean)
              .map((c, i) => (
                <FeaturedCard
                  bgColor={secondaryColor}
                  borderColor={primaryColor}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  card={c!}
                  key={i}
                  horizontal
                />
              ))}
          </div>
        </section>

        <section className='mt-20'>
          <h4 className='font-semibold text-3xl leading-10'>Benefícios Exclusivos da Parceria</h4>
          <p className='font-light text-lg pt-2 leading-tight'>
            <span>
              Você terá um{' '}
              <strong className='font-semibold' style={{ color: primaryColor }}>
                DESCONTO EXCLUSIVO
              </strong>{' '}
              para contratar nossos planos e acesso a benefícios imperdíveis para o seu negócio.
            </span>
            {hasFreeDigitalCertificate && (
              <span>
                {' '}
                Ganhe até um{' '}
                <strong className='font-semibold' style={{ color: primaryColor }}>
                  CERTIFICADO DIGITAL GRATUITO
                </strong>{' '}
                ao{' '}
                <strong className='font-semibold' style={{ color: primaryColor }}>
                  ABRIR UMA EMPRESA
                </strong>
                !
              </span>
            )}
            <span> Sem nenhum tipo de vínculo ou multa caso deseje cancelar.</span>
          </p>

          {data.lp?.pricing && (
            <div className='my-8'>
              <Plans
                pricing={data.lp?.pricing}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                benefitsCardOptions={{
                  wrapped: true,
                }}
              />
            </div>
          )}
        </section>

        {lp.contactForm && (
          <section id='contato'>
            <ContactForm
              variant='landing-page'
              title={lp.contactForm.title || ''}
              description={lp.contactForm.description || ''}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              marketingCampaignCode={lp.campaignCode || undefined}
              onError={handleContactError}
              cnpjRequired={lp.contactForm.cnpjRequired || false}
            />

            <Dialog
              isOpen={isPartnershipDisabledDialogOpen}
              onClose={() => setIsPartnershipDisabledDialogOpen(false)}
              title={`Parceria exclusiva para clientes ${lp.partner?.name}`}
              description=''
              submitButtonlabel='Acessar'
              onSubmit={handlePartnershipDisabledClick}
              content={
                <article
                  className='pt-5 prose prose-sm'
                  dangerouslySetInnerHTML={{ __html: lp.contactForm?.partnershipNotEnabledContent?.data?.childMarkdownRemark?.html || '' }}
                ></article>
              }
            />
          </section>
        )}

        <section>
          <FAQ
            questions={(globalData?.faq as Queries.FAQInfoFragment[]) || []}
            extraQuestions={(lp.extraFaq as Queries.FAQInfoFragment[]) || []}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          />
        </section>
      </Container>
    </LandingPageLayout>
  )
}

export const query = graphql`
  fragment PlansContent on STRAPI__COMPONENT_PRICING_PLAN_PRICE {
    id
    valueFrom
    value
    monthly
    # highlightChipText
  }

  fragment PricingInfo on STRAPI_LANDING_PAGE {
    pricing {
      id
      meiCommerce {
        ...PlansContent
      }
      meiService {
        ...PlansContent
      }
      simplesNacionalCommerce {
        ...PlansContent
      }
      simplesNacionalService {
        ...PlansContent
      }
      otherCommerce {
        ...PlansContent
      }
      otherService {
        ...PlansContent
      }
      digitalCertificate {
        ...PlansContent
      }
    }
  }

  query LandingPage($slug: String!) {
    globalData: strapiGlobal {
      faq {
        ...FAQInfo
      }
    }

    lp: strapiLandingPage(slug: { eq: $slug }) {
      id
      title
      slug
      subtitle
      highlightText
      primaryColor
      secondaryColor
      headerLogo {
        localFile {
          childImageSharp {
            gatsbyImageData(
              width: 200
              aspectRatio: 1.7777777777
              backgroundColor: "white"
              transformOptions: { cropFocus: CENTER, fit: CONTAIN }
            )
          }
        }
      }
      partner {
        id
        name
      }
      primarySectionTitle
      primarySectionText
      primaryCards {
        ...FeaturedCard
      }
      secondarySectionText
      secondaryCards {
        ...FeaturedCard
      }
      highlightImage {
        localFile {
          childImageSharp {
            gatsbyImageData(height: 250, aspectRatio: 1)
          }
        }
      }
      campaignCode
      contactForm {
        title
        description
        cnpjRequired
        partnershipNotEnabledLink
        partnershipNotEnabledContent {
          data {
            childMarkdownRemark {
              id
              html
            }
          }
        }
      }
      extraFaq {
        ...FAQInfo
      }
      ...PricingInfo
    }
  }
`

export default PartnershipLandingPage
